import { Box, Button, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";

import ConfirmationPopup from "components/common/ConfirmationPopup";
import { FaxProfileStatus, UpdateFaxProfileStatus } from "hooks/useFaxProfile";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface FaxProfileActionsProps {
  status: FaxProfileStatus;
  loading?: boolean;
  onChangeStatus: (status: UpdateFaxProfileStatus) => void;
}

type PendingAction = UpdateFaxProfileStatus | null;

interface ActionCopy {
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
}

const FaxProfileActions: React.FC<FaxProfileActionsProps> = ({
  status,
  loading = false,
  onChangeStatus,
}) => {
  const { colors } = useCelleraTheme();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const COPY: Record<UpdateFaxProfileStatus, ActionCopy> = useMemo(
    () => ({
      suspended: {
        title: "Suspend fax profile?",
        message:
          "Inbound faxes will be rejected and the Send Fax action will be hidden until you reactivate.",
        confirmLabel: "Suspend",
        confirmColor: colors.warning[700],
      },
      active: {
        title: "Reactivate fax profile?",
        message:
          "Cellera will re-validate the Sinch service binding. If validation fails, the profile will stay suspended.",
        confirmLabel: "Reactivate",
        confirmColor: colors.primary.main,
      },
      retired: {
        title: "Retire fax profile?",
        message:
          "Retired profiles cannot be edited or reused. This action cannot be undone.",
        confirmLabel: "Retire",
        confirmColor: colors.error[500],
      },
    }),
    [colors],
  );

  if (status === "retired") {
    return (
      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
        This profile is retired and cannot be changed.
      </Typography>
    );
  }

  const renderButtons = () => {
    if (status === "active") {
      return (
        <>
          <Button
            variant="outlined"
            sx={{
              color: colors.warning[700],
              borderColor: colors.warning[500],
              "&:hover": {
                borderColor: colors.warning[700],
                backgroundColor: colors.warning[50],
              },
            }}
            onClick={() => setPendingAction("suspended")}
            disabled={loading}
            aria-haspopup="dialog"
          >
            Suspend
          </Button>
          <Button
            variant="text"
            sx={{ color: colors.error[500] }}
            onClick={() => setPendingAction("retired")}
            disabled={loading}
            aria-haspopup="dialog"
          >
            Retire
          </Button>
        </>
      );
    }

    if (status === "draft") {
      return (
        <Button
          variant="text"
          sx={{ color: colors.error[500] }}
          onClick={() => setPendingAction("retired")}
          disabled={loading}
          aria-haspopup="dialog"
        >
          Retire
        </Button>
      );
    }

    return (
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setPendingAction("active")}
          disabled={loading}
          aria-haspopup="dialog"
        >
          Reactivate
        </Button>
        <Button
          variant="text"
          sx={{ color: colors.error[500] }}
          onClick={() => setPendingAction("retired")}
          disabled={loading}
          aria-haspopup="dialog"
        >
          Retire
        </Button>
      </>
    );
  };

  const activeCopy = pendingAction ? COPY[pendingAction] : null;

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        {renderButtons()}
      </Box>

      <ConfirmationPopup
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={() => {
          if (pendingAction) {
            onChangeStatus(pendingAction);
            setPendingAction(null);
          }
        }}
        title={activeCopy?.title ?? ""}
        message={activeCopy?.message ?? ""}
        confirmText={activeCopy?.confirmLabel ?? "Confirm"}
        confirmColor={activeCopy?.confirmColor}
        loading={loading}
      />
    </>
  );
};

export default FaxProfileActions;
